import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ReceiptPercentIcon,
  PhotoIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CameraIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Redemption = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restaurantIdFromUrl = searchParams.get('restaurant');

  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Restaurant & Bill
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [billImage, setBillImage] = useState(null);
  const [billPreview, setBillPreview] = useState(null);
  const [billBase64, setBillBase64] = useState(null);

  // Bill details
  const [billDetails, setBillDetails] = useState(null);
  const [manualAmount, setManualAmount] = useState('');

  // Step 2: Payment
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentPreview, setPaymentPreview] = useState(null);
  const [paymentBase64, setPaymentBase64] = useState(null);
  const [upiTransactionId, setUpiTransactionId] = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (restaurantIdFromUrl && restaurants.length > 0) {
      const restaurant = restaurants.find(r => r._id === restaurantIdFromUrl);
      if (restaurant) {
        setSelectedRestaurant(restaurant);
      }
    }
  }, [restaurantIdFromUrl, restaurants]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  // Compress image to stay within Vercel's 4.5MB body limit
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleBillImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }
      setBillImage(file);
      const base64 = await compressImage(file);
      setBillPreview(base64);
      setBillBase64(base64);
      setError('');
    }
  };

  const handlePaymentImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }
      setPaymentScreenshot(file);
      const base64 = await compressImage(file);
      setPaymentPreview(base64);
      setPaymentBase64(base64);
      setError('');
    }
  };

  // Step 1: Calculate discount from manual amount
  const handleCalculateDiscount = () => {
    if (!selectedRestaurant) {
      setError('Please select a restaurant');
      return;
    }
    if (!manualAmount || parseFloat(manualAmount) <= 0) {
      setError('Please enter a valid bill amount');
      return;
    }

    setError('');

    const amount = parseFloat(manualAmount);
    const discountPercentage = selectedRestaurant.discountPercentage || 20;
    const discountAmount = Math.round((amount * discountPercentage) / 100);
    const availablePoints = user?.currentRewardBalance || 0;
    const actualDiscount = Math.min(discountAmount, availablePoints);

    setBillDetails({
      originalAmount: amount,
      discountPercentage,
      maxDiscount: discountAmount,
      actualDiscount,
      finalAmount: amount - actualDiscount,
      pointsRequired: actualDiscount,
      pointsAvailable: availablePoints,
      insufficientPoints: actualDiscount < discountAmount
    });
    setCurrentStep(2);
  };

  // Step 2: Submit redemption
  const handleSubmitRedemption = async () => {
    if (!billDetails) {
      setError('Bill details missing');
      return;
    }
    if (paymentMethod === 'upi' && !paymentBase64) {
      setError('Please upload payment screenshot');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/transactions`, {
        restaurantId: selectedRestaurant._id,
        billImage: billBase64 || undefined,
        originalBillAmount: billDetails.originalAmount,
        paymentMethod,
        paymentScreenshot: paymentBase64 || undefined,
        upiTransactionId: upiTransactionId || undefined
      });

      setSuccessData(response.data);
      setSuccess(true);

      // Refresh user data to update points
      if (refreshUser) refreshUser();

    } catch (error) {
      console.error('Submit redemption error:', error);
      setError(error.response?.data?.message || 'Failed to submit redemption');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center">
          <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {successData?.paymentVerified ? 'Redemption Complete!' : 'Redemption Submitted!'}
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            {successData?.message}
          </p>
          {successData?.newPointBalance !== undefined && (
            <p className="text-md text-gray-500 mb-8">
              Your new point balance: <span className="font-bold text-primary-600">{successData.newPointBalance}</span>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/transactions')}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              View My Transactions
            </button>
            <button
              onClick={() => navigate('/neighborhood')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Back to Restaurants
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => currentStep === 1 ? navigate(-1) : setCurrentStep(1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {currentStep === 1 ? 'Back' : 'Back to Step 1'}
          </button>
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-100">
              <ReceiptPercentIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Redeem Points</h1>
              <p className="text-gray-600 mt-1">Available Points: {user?.currentRewardBalance || 0}</p>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
            1
          </div>
          <div className={`w-20 h-1 ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
            2
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <XMarkIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* STEP 1: Restaurant & Bill Details */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Step 1: Enter Bill Details</h2>

            {/* Restaurant Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Restaurant *
              </label>
              <select
                value={selectedRestaurant?._id || ''}
                onChange={(e) => {
                  const restaurant = restaurants.find(r => r._id === e.target.value);
                  setSelectedRestaurant(restaurant);
                }}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="">Choose a restaurant...</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant._id} value={restaurant._id}>
                    {restaurant.name} - {restaurant.discountPercentage || 20}% discount
                  </option>
                ))}
              </select>
            </div>

            {/* Bill Amount Entry */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bill Amount (₹) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-semibold">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="Enter total bill amount"
                  className="w-full border border-gray-300 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                />
              </div>
            </div>

            {/* Optional Bill Photo */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Upload Bill Photo (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center">
                {billPreview ? (
                  <div className="relative">
                    <img
                      src={billPreview}
                      alt="Bill"
                      className="max-h-48 mx-auto rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setBillImage(null);
                        setBillPreview(null);
                        setBillBase64(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                      <CameraIcon className="h-4 w-4" />
                      Attach Bill Photo
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleBillImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Calculate Discount Button */}
            <button
              onClick={handleCalculateDiscount}
              disabled={!manualAmount || parseFloat(manualAmount) <= 0 || !selectedRestaurant}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-700 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Calculate Discount
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* STEP 2: Payment */}
        {currentStep === 2 && billDetails && (
          <div className="space-y-6">
            {/* Discount Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Discount Breakdown</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Bill Amount:</span>
                  <span className="font-semibold">₹{billDetails.originalAmount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Your Discount ({billDetails.discountPercentage}%):</span>
                  <span className="font-semibold">-₹{billDetails.actualDiscount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-blue-600">
                  <span>Points Used:</span>
                  <span className="font-semibold">{billDetails.pointsRequired}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 mt-3 flex justify-between text-xl">
                  <span className="font-bold text-gray-900">Amount to Pay:</span>
                  <span className="font-bold text-primary-600">₹{billDetails.finalAmount?.toFixed(2)}</span>
                </div>
              </div>

              {billDetails.insufficientPoints && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    You don't have enough points for the full {billDetails.discountPercentage}% discount.
                    Using all {billDetails.pointsAvailable} available points.
                  </p>
                </div>
              )}
            </div>

            {/* Payment Method Selection */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Step 2: Complete Payment</h2>

              <p className="text-gray-600 mb-4">
                Pay <span className="font-bold text-primary-600">₹{billDetails.finalAmount?.toFixed(2)}</span> at the restaurant
              </p>

              {/* Payment Method Toggle */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setPaymentMethod('upi')}
                  className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition ${
                    paymentMethod === 'upi'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <DevicePhoneMobileIcon className="h-6 w-6" />
                  <span className="font-semibold">UPI Payment</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('cash')}
                  className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition ${
                    paymentMethod === 'cash'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <BanknotesIcon className="h-6 w-6" />
                  <span className="font-semibold">Cash Payment</span>
                </button>
              </div>

              {/* UPI Payment: Screenshot Upload */}
              {paymentMethod === 'upi' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Payment Screenshot *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                      {paymentPreview ? (
                        <div className="relative">
                          <img
                            src={paymentPreview}
                            alt="Payment screenshot"
                            className="max-h-64 mx-auto rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentScreenshot(null);
                              setPaymentPreview(null);
                              setPaymentBase64(null);
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <CameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                          <label className="cursor-pointer">
                            <span className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition">
                              <PhotoIcon className="h-5 w-5" />
                              Upload Screenshot
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handlePaymentImageChange}
                              className="hidden"
                            />
                          </label>
                          <p className="text-sm text-gray-500 mt-3">
                            Upload your PhonePe/GPay/Paytm payment screenshot
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      UPI Transaction ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={upiTransactionId}
                      onChange={(e) => setUpiTransactionId(e.target.value)}
                      placeholder="e.g., 123456789012"
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">Helps verify faster</p>
                  </div>
                </div>
              )}

              {/* Cash Payment: Info */}
              {paymentMethod === 'cash' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Cash Payment</h3>
                  <p className="text-sm text-blue-800">
                    Pay ₹{billDetails.finalAmount?.toFixed(2)} in cash at the restaurant.
                    The merchant will confirm your payment and your points will be deducted.
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmitRedemption}
                disabled={submitting || (paymentMethod === 'upi' && !paymentBase64)}
                className="w-full mt-6 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-700 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-6 w-6" />
                    Complete Redemption
                  </>
                )}
              </button>
            </div>

            {/* How it works */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                {paymentMethod === 'upi' ? (
                  <>
                    <li>Your points are deducted instantly</li>
                    <li>Merchant will be notified of your redemption</li>
                    <li>Payment screenshot is stored as proof</li>
                  </>
                ) : (
                  <>
                    <li>Merchant will receive notification to confirm your cash payment</li>
                    <li>Once confirmed, your points will be deducted</li>
                    <li>You'll receive a notification when confirmed</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Redemption;
